import { BLOCK_PARAGRAPH, isBlockEmpty, moveToNextLeafBlock, updates } from '@gitbook/doc-core';
import { type Change, isBlockRangeSelection } from '@gitbook/slate';
import type { Plugin } from '@gitbook/slate-react';

import { isBackspace, isDelete, isEnter, isModEnter } from '../common';

export const plugin: Plugin = {
    onKeyDown,
};

/**
 * Handle key down events for update blocks.
 */
function onKeyDown(event: KeyboardEvent, change: Change): Change | undefined {
    if (isBlockRangeSelection(change.value.selection)) {
        return;
    }

    if (isModEnter(event)) {
        return onModEnter(event, change);
    }

    if (isEnter(event)) {
        return onEnter(event, change);
    }

    if (isBackspace(event) || isDelete(event)) {
        return onDelete(event, change);
    }

    return undefined;
}

/**
 * Pressing enter in a update block.
 */
function onEnter(_event: KeyboardEvent, change: Change): Change | undefined {
    const current = updates.getCurrentUpdate(change.value);

    if (!current) {
        return undefined;
    }

    const lastBlock = current.update.blockNodes.last();

    // Last block is a paragraph and cursor is at the end of it.
    if (
        lastBlock &&
        lastBlock.type === BLOCK_PARAGRAPH &&
        isBlockEmpty(lastBlock) &&
        change.value.selection.hasAnchorAtEndOf(lastBlock)
    ) {
        // Remove last block.
        change.removeNodeByKey(lastBlock.key);

        // Insert a new update.
        const currentStepIndex = current.updates.getChildIndex(current.update.key);
        const newStep = updates.createUpdate();
        change.insertNodeByKey(current.updates.key, currentStepIndex + 1, newStep);
        change.collapseToStartOf(newStep);
        return change;
    }

    return undefined;
}

/**
 * Pressing backspace or delete in a update block.
 */
function onDelete(_event: KeyboardEvent, change: Change): Change | undefined {
    const current = updates.getCurrentUpdate(change.value);

    if (!current) {
        return undefined;
    }

    const firstBlock = current.update.blockNodes.first();

    // There is only one empty node.
    if (
        current.update.nodes.size === 1 &&
        firstBlock &&
        isBlockEmpty(firstBlock) &&
        change.value.selection.hasAnchorAtStartOf(firstBlock)
    ) {
        change.removeNodeByKey(current.update.key);
        return change;
    }

    return undefined;
}

/**
 * Pressing mod+enter, exit the whole update block.
 */
function onModEnter(event: KeyboardEvent, change: Change): Change | undefined {
    const { value } = change;

    if (!value.isCollapsed) {
        return;
    }

    const current = updates.getCurrentUpdate(change.value);
    if (!current) {
        return;
    }

    event.preventDefault();

    moveToNextLeafBlock(change, current.updates);
    return change;
}
